<template>
  <div class="w-full">
    <!-- Label:start -->
    <label v-if="label.trim() !== ''" class="form-label" :for="inputId">
      {{ label }}
    </label>
    <!-- Label:end -->

    <!-- Input+button:start -->
    <div v-if="!isMax" class="input-group">
      <input
        v-model="currentInput"
        :id="inputId"
        type="text"
        class="form-control"
        :class="[
          inputTooLong || inputError ? 'border border-theme-6' : '',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ]"
        :placeholder="placeholder"
        :aria-describedby="`${inputId}-add-btn`"
        :maxlength="maxLength"
        :disabled="disabled"
        @keyup="addItem($event)"
        @focusout="addItem($event)"
      />

      <div
        :id="`${inputId}-add-btn`"
        class="input-group-text btn"
        :class="[disabled || isMax ? 'opacity-50 cursor-not-allowed pointer-events-none' : '']"
        @click="addMultipleItem"
      >
        <PlusIcon />
      </div>
    </div>
    <!-- Input+button:end -->

    <div v-else-if="maxItemsText">{{ maxItemsText }} ({{ maxItems }}).</div>

    <!-- Counter words:start -->
    <div
      v-if="!isMax"
      class="text-xs mt-2 whitespace-nowrap"
      :class="inputTooLong ? 'text-red-500 font-semibold' : 'text-gray-500'"
    >
      {{ currentInput.length }}/{{ maxLength }}
    </div>
    <!-- Counter words:end -->

    <!-- List items:start -->
    <div class="mt-5" v-if="internalList.length > 0">
      <div class="flex items-center" v-for="(item, idx) in internalList" :key="item.id ?? idx">
        <!-- Read mode:start -->
        <div v-if="!item.edit" class="items-center w-full relative">
          <div class="intro-x">
            <div class="box border px-5 mb-3 flex items-center">
              <div class="ml-4 mr-auto flex">
                <div v-if="!isURL" class="pill__text" :class="{ pill__to__delete: item.delete }">
                  {{ item[itemKey] }}
                </div>
                <a
                  v-else
                  :href="item[itemKey]"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="pill__text"
                  :class="{ pill__to__delete: item.delete }"
                >
                  <LinkIcon v-if="isURL" class="btn-source-icon link__icon" />
                  {{ item[itemKey] }}
                </a>
              </div>

              <button
                v-if="!item.delete"
                type="button"
                class="ml-5 btn-outline-dark btn-rounded btn-sm"
                @click="toggleEdit(item)"
              >
                <EditIcon class="btn-source-icon" />
              </button>

              <button
                v-if="!item.delete"
                type="button"
                class="btn-outline-dark btn-rounded btn-sm"
                @click="toggleDelete(item)"
              >
                <Trash2Icon class="btn-source-icon" />
              </button>

              <button
                v-else
                @click="toggleDelete(item, false)"
                type="button"
                class="btn-outline-dark btn-rounded btn-sm"
                title="Restaurar"
              >
                <RotateCcwIcon />
              </button>
            </div>
          </div>
        </div>
        <!-- Read mode:end -->

        <!-- Edit mode:start -->
        <div v-else class="block w-full">
          <div class="input-group w-full mb-3">
            <input
              type="text"
              class="form-control"
              :placeholder="editPlaceholder || placeholder"
              :aria-describedby="`${inputId}-save-btn-${idx}`"
              v-model="item[itemKey]"
              :maxlength="maxLength"
              @keyup.enter="toggleEdit(item, false)"
            />
            <div :id="`${inputId}-save-btn-${idx}`" class="input-group-text btn" @click="toggleEdit(item, false)">
              <SaveIcon />
            </div>
          </div>

          <!-- Counter words edit mode:start -->
          <div
            class="text-xs mt-1 whitespace-nowrap"
            :class="(item[itemKey]?.length || 0) > maxLength ? 'text-red-500 font-semibold' : 'text-green-500'"
          >
            {{ item[itemKey]?.length || 0 }}/{{ maxLength }}
          </div>
          <!-- Counter words edit mode:end -->

          <!-- Error edición -->
          <div v-if="item.error" class="text-theme-6 mt-2">
            {{ editErrorText }}
          </div>
        </div>
        <!-- Edit mode:end -->
      </div>
    </div>
    <!-- List items:end -->

    <!-- Global error message:start -->
    <div v-if="error" class="text-theme-6 mt-2">
      {{ error }}
    </div>
    <!-- Global error message:end -->
  </div>
</template>

<script>
export default {
  name: "MultipleTextList",
  props: {
    /**
     * v-model: list of items. Accepts:
     * - Array of objects: [{ id?, link/text: string, ... }]
     * - Array of strings: ['https://...'] (normalized to objects)
     */
    modelValue: { type: Array, default: () => [] },

    // Key of the text field within each item (e.g., "link"). Defaults to "text".
    itemKey: { type: String, default: "text" },

    // Label above the input
    label: { type: String, default: "" },

    // Placeholder of input
    placeholder: {
      type: String,
      default: "Pega un enlace y presiona Enter",
    },

    // Specific placeholder for editing (optional)
    editPlaceholder: { type: String, default: "" },

    // Error text when trying to save empty in editing
    editErrorText: { type: String, default: "Campo no valido." },

    // Character limit per item
    maxLength: { type: Number, default: 255 },

    // Maximum limit of items (optional). null/0 = no limit
    maxItems: { type: Number, default: 0 },

    // Text to show when max items is reached (optional)
    maxItemsText: { type: String, default: "" },

    // Disable the entire component
    disabled: { type: Boolean, default: false },

    // Global error message
    error: { type: String, default: "" },

    // Duplicates: if true, prevents adding the same value (case-sensitive)
    dedupe: { type: Boolean, default: true },

    // If true, validates that the input is a valid URL
    isURL: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "add", "edit", "delete"],
  data() {
    return {
      inputId: `lrl-${global.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`,
      currentInput: "",
      inputError: false,
    };
  },
  computed: {
    internalList: {
      get() {
        const list = Array.isArray(this.modelValue) ? this.modelValue : [];
        return list.map((it) => {
          if (typeof it === "string") {
            return { id: null, [this.itemKey]: it, edit: false, delete: false, error: false };
          }
          // Ensure minimum flags
          return {
            id: it.id ?? null,
            ...it,
            edit: !!it.edit,
            delete: !!it.delete,
            error: !!it.error,
          };
        });
      },
      set(next) {
        // Emit "clean" copy
        const clean = next.map((it) => ({
          id: it.id ?? null,
          ...it,
          edit: !!it.edit,
          delete: !!it.delete,
          error: !!it.error,
        }));
        this.$emit("update:modelValue", clean);
      },
    },

    isMax() {
      return this.maxItems > 0 && this.internalList.filter((it) => !it.delete).length >= this.maxItems;
    },
    isNearMax() {
      return this.maxItems > 0 && this.internalList.filter((it) => !it.delete).length >= this.maxItems - 1;
    },
    inputTooLong() {
      return this.currentInput.length > this.maxLength;
    },
  },
  methods: {
    // --- ADD item ---
    addItem(e) {
      if (this.disabled) return;

      const srcIsEnter = e && e.type === "keyup" && e.key === "Enter";
      const srcIsBlur = e && e.type === "focusout";
      const srcIsClick =
        (e && (e.type === "click" || e.pointerId !== undefined)) || e === "click" || e?.source === "btn";

      if (!(srcIsEnter || srcIsBlur || srcIsClick)) return;
      if (this.isMax) return;

      const raw = (this.currentInput || "").trim();
      if (!raw) return;

      // Basic URL validation
      if (this.isURL && !this.validateURL(raw)) {
        this.inputError = true;
        return;
      } else {
        this.inputError = false;
      }

      // dedupe
      if (this.dedupe) {
        const exists = this.internalList.some((it) => (it[this.itemKey] || "") === raw);
        if (exists) {
          this.currentInput = "";
          return;
        }
      }

      const next = [
        ...this.internalList,
        {
          id: null,
          [this.itemKey]: raw,
          edit: false,
          delete: false,
          error: false,
        },
      ];

      this.internalList = next;
      this.currentInput = "";
      this.$emit("add", raw);
    },

    // --- ADD multiple items (from button) ---
    addMultipleItem() {
      const raw = (this.currentInput || "").trim();
      if (!raw) return;

      const lines = raw
        .split(",")
        .map((l) => l.trim())
        .filter((l) => l !== "");

      const newLines = [];
      const isMaxWithNewLines = () =>
        this.maxItems > 0 && this.internalList.filter((it) => !it.delete).length + newLines.length >= this.maxItems;

      let l = "";
      while (lines.length > 0) {
        l = lines.shift();

        // Validate limit
        if (isMaxWithNewLines()) break;

        l = l.trim();
        if (l === "") continue;

        // Basic URL validation
        if (this.isURL && !this.validateURL(l)) {
          this.inputError = true;
          break;
        } else {
          this.inputError = false;
        }

        // dedupe
        if (this.dedupe) {
          const exists = [...this.internalList, ...newLines].some((it) => (it[this.itemKey] || "") === l);

          if (exists) {
            continue;
          }
        }

        const next = {
          id: null,
          [this.itemKey]: l,
          edit: false,
          delete: false,
          error: false,
        };
        newLines.push(next);

        this.$emit("add", l);
        l = "";
      }

      this.internalList = [...this.internalList, ...newLines];

      // Re-add the last unprocessed line (if any)
      if (l !== "") lines.unshift(l);

      this.currentInput = lines.join(", ");
    },

    // --- EDIT item ---
    toggleEdit(item, editMode = true) {
      if (!editMode) {
        const val = (item[this.itemKey] || "").trim();

        if (val === "" || val.length > this.maxLength) {
          item.error = true;
          this.reactivityFix(item);
          return;
        }

        if (this.isURL && !this.validateURL(val)) {
          item.error = true;
          this.reactivityFix(item);
          return;
        }

        item[this.itemKey] = val;
        item.error = false;
        item.edit = false;
        this.$emit("edit", { item });

        this.reactivityFix(item);
      } else {
        item.edit = true;
        item.error = false;

        this.reactivityFix(item);
      }
    },

    // --- DELETE item ---
    toggleDelete(item, deleteMode = true) {
      if (!deleteMode && this.isMax) return;

      item.delete = deleteMode;
      this.$emit("delete", { item });

      this.reactivityFix(item);
    },

    // Force reactivity for objects within arrays
    reactivityFix(item) {
      this.internalList = this.internalList.map((it) => (it === item ? { ...item } : it));
    },

    // Simple URL validation
    validateURL(url = "") {
      url = url.trim();

      // If empty, do not mark as error (false = no error)
      if (url === "") {
        return false;
      }

      // Pattern that requires http:// or https:// at the beginning
      const pattern = new RegExp(
        "^(https?:\\/\\/)" + // required
          "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
          "((\\d{1,3}\\.){3}\\d{1,3}))" +
          "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
          "(\\?[;&a-z\\d%_.~+=-]*)?" +
          "(\\#[-a-z\\d_]*)?$",
        "i"
      );

      const result = pattern.test(url);
      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
.link__icon {
  transform: translateX(-50%) scale(0.9);
}

.pill__text {
  overflow-wrap: break-word;
  font-weight: 500;
  opacity: 1;
  transition: opacity 0.3s ease;

  &.pill__to__delete {
    text-decoration: line-through;
    opacity: 0.5;
    font-weight: 400;
    color: #a0aec0;
  }
}
</style>
